<template>
	<div class="exports-table">
		<ApiTable
			v-model:expanded-rows="expandedRows"
			clickable
			:list-state="props.listState"
			:loading="false"
			@row-click="toggleExpand"
			@update:pagination-page="emit('page-change')">
			<Column
				expander
				:style="{ width: '20px' }" />
			<Column header="App">
				<template #body="slotProps">
					<div
						:id="`export-${slotProps.data.id}`"
						class="exports-table__app">
						<div class="exports-table__app-name">
							{{ slotProps.data.build?.app?.name ?? 'Unknown app' }}
						</div>
						<span class="exports-table__app-uuid">{{ slotProps.data.uuid }}</span>
					</div>
				</template>
			</Column>
			<Column
				header="Version"
				:header-style="{ display: 'flex', 'justify-content': 'center' }">
				<template #body="slotProps">
					<div class="exports-table__version">
						<Tag
							v-if="slotProps.data.build?.version != null"
							severity="info"
							:value="`v${slotProps.data.build.version}`" />
						<span
							v-else
							class="exports-table__muted"
							>–</span
						>
					</div>
				</template>
			</Column>
			<Column header="Export date">
				<template #body="slotProps">
					<div class="exports-table__date">
						<span class="exports-table__date-date">{{
							formatHuman(slotProps.data.created_at)
						}}</span>
						<router-link
							class="exports-table__date-link"
							:to="{
								name: 'account-details',
								query: { tab: 'usage', s: slotProps.data.uuid },
							}"
							>View used tokens</router-link
						>
					</div>
				</template>
			</Column>
			<Column header="">
				<template #body="slotProps">
					<div class="exports-table__actions">
						<Button
							v-tooltip.top="'Download zip'"
							icon="fal fa-download"
							rounded
							text
							@click.stop="downloadZip(slotProps.data)" />
						<Button
							v-tooltip.top="'Push to GitHub'"
							icon="fal fa-cloud-arrow-up"
							rounded
							text
							@click.stop="openRepoMenu($event, slotProps.data)" />
						<SendEmailDownloadButton
							compact
							:build-id="slotProps.data.build_id" />
						<Menu
							:ref="(el) => setMenuRef(Number(slotProps.data.id), el)"
							:model="menuModels[Number(slotProps.data.id)] ?? []"
							:popup="true" />
						<!--Button
							v-tooltip.top="'Preview version'"
							icon="fal fa-eye"
							text
							rounded
							@click.stop="viewBuild(slotProps.data)" /-->
					</div>
				</template>
			</Column>

			<template #expansion="slotProps">
				<div class="exports-table__expansion">
					<DeliveryDataTable
						data-key="id"
						size="small"
						:value="slotProps.data.code_deliveries ?? []">
						<DeliveryColumn header="Delivery">
							<template #body="d">
								<div class="exports-table__delivery">
									<span class="exports-table__delivery-type">{{ d.data.type }}</span>
								</div>
							</template>
						</DeliveryColumn>
						<DeliveryColumn header="Date">
							<template #body="d">
								<span class="exports-table__delivery-date">{{
									formatHuman(d.data.created_at)
								}}</span>
							</template>
						</DeliveryColumn>
						<DeliveryColumn header="Destination">
							<template #body="d">
								<span
									v-if="d.data.type === 'git' && d.data.deliverable_url"
									class="exports-table__destination exports-table__destination--git">
									<FontAwesomeIcon icon="fab fa-github" />
									<a
										target="blank"
										:href="d.data.deliverable_url"
										>{{ getRepoName(d.data.deliverable_url) }}</a
									>
								</span>
								<span
									v-else
									class="exports-table__destination exports-table__destination--zip">
									<FontAwesomeIcon icon="fal fa-file-archive" />
									<a :href="d.data.deliverable_url">File Download</a>
								</span>
							</template>
						</DeliveryColumn>
						<DeliveryColumn
							header="Status"
							:style="{ width: '180px' }">
							<template #body="d">
								<Tag
									:key="statusIcon(d.data.status)"
									class="exports-table__status-tag"
									:icon="statusIcon(d.data.status)"
									:pt="
										d.data.status === 'failed' && d.data.failure_reason
											? { root: { 'data-p-tooltip': d.data.failure_reason } }
											: {}
									"
									:severity="statusSeverity(d.data.status)"
									:value="statusLabel(d.data.status)" />
							</template>
						</DeliveryColumn>
					</DeliveryDataTable>
				</div>
			</template>
		</ApiTable>
	</div>
</template>

<script setup lang="ts">
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import { computed, nextTick, onBeforeMount, onUnmounted, ref, watch } from 'vue'
import { format } from 'date-fns'
import { useRoute, useRouter } from 'vue-router'
import CodeDeliveriesApi from '@/models/CodeDelivery/Api'
import type ListState from '@/helpers/models/ListState'
import type { ExportModel, ExportStorePayload, ExportUpdatePayload } from '@/models/Export/Model'
import type { LaravelPaginationResponse } from '@/interfaces/models/Laravel'
import Menu from 'primevue/menu'
import * as Sentry from '@sentry/browser'
import type { IApi } from '@/helpers/models/Api'
import useApiTable from '@/components/Table/useApiTable'
import type { CodeDelivery } from '@/models/CodeDelivery/Model'
import useDataTable from '@/components/primevue/useDataTable'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'
import SendEmailDownloadButton from '@/views/Admin/EmailDownloads/components/SendEmailDownloadButton.vue'

const props = defineProps<{
	listState: ListState<
		IApi<
			ExportModel,
			LaravelPaginationResponse<ExportModel>,
			ExportStorePayload,
			ExportUpdatePayload
		>,
		ExportModel,
		LaravelPaginationResponse<ExportModel>
	>
}>()
const { ApiTable, Column } = useApiTable(props.listState)
const { DataTable: DeliveryDataTable, Column: DeliveryColumn } = useDataTable<CodeDelivery>()

defineExpose({
	ensureRowOpen,
})

const emit = defineEmits<{
	(e: 'page-change'): void
}>()

const router = useRouter()
const route = useRoute()
const expandedRows = ref<any[]>([])
const { refresh } = useInterval()
const menus = ref<Record<number, any>>({})
const menuModels = ref<Record<number, any[]>>({})

onBeforeMount(async () => {
	await refresh()
})

watch(() => route.query.exportId, openRowFromQueryParams, { immediate: true })

function formatHuman(date: string) {
	const d = new Date(date)
	return format(d, 'PPpp')
}

function toggleExpand(event: any) {
	const row = event.data
	const index = expandedRows.value.findIndex((r) => r.id === row.id)
	if (index >= 0) expandedRows.value.splice(index, 1)
	else expandedRows.value.push(row)
}

function statusLabel(status: 'pending' | 'success' | 'failed') {
	if (status === 'success') return 'Success'
	if (status === 'failed') return 'Error'
	return 'Delivering'
}
function statusSeverity(status: 'pending' | 'success' | 'failed') {
	if (status === 'success') return 'success'
	if (status === 'failed') return 'danger'
	return 'warn'
}
function statusIcon(status: 'pending' | 'success' | 'failed') {
	if (status === 'success') return 'fal fa-check'
	if (status === 'failed') return 'fal fa-exclamation-triangle'
	return 'fal fa-spinner fa-spin'
}

async function downloadZip(exportModel: any) {
	ensureRowOpen(exportModel.id)
	try {
		await new CodeDeliveriesApi().deliver({
			build_id: exportModel.build_id,
			delivery: { type: 'zip' },
		})
	} catch (err: unknown) {
		Sentry.captureException(err)
	}
	refresh()
}

function setMenuRef(id: number, el: any) {
	if (!el) return
	menus.value[id] = el
}

function openRepoMenu(event: MouseEvent, exportModel: any) {
	const repos = exportModel.build?.app?.repository_configs ?? []
	const items = [
		...repos.map((r: any) => ({
			label: getRepoName(r.github_repository_url),
			icon: 'fab fa-github',
			command: async () => {
				ensureRowOpen(exportModel.id)
				try {
					await new CodeDeliveriesApi().deliver({
						build_id: exportModel.build_id,
						delivery: { type: 'git', repository_config_id: r.id },
					})
				} catch (err: unknown) {
					Sentry.captureException(err)
				}
				refresh()
			},
		})),
	]
	if (repos.length > 0) items.push({ separator: true })
	items.push({
		label: 'Add repository',
		icon: 'fal fa-plus',
		command: () => {
			router.push({
				name: 'apps-details',
				params: { id: exportModel.build.app_id },
				query: { tab: 'settings' },
			})
		},
	})
	menuModels.value[exportModel.id] = items
	menus.value[exportModel.id]?.toggle(event)
}

//function viewBuild(exportModel: any) {
//	const appId = exportModel.build?.app_id
//	if (appId) router.push({ name: 'apps-details', params: { id: appId } })
//}

function getRepoName(url: string) {
	const match = url.match(/github\.com\/(.+)$/)
	return match ? match[1] : url
}

function useInterval() {
	let statusInterval: any
	const intervalShouldBeRunning = computed(() => {
		return props.listState.list.value.find((exportModel: any) => {
			return exportModel.code_deliveries?.find((delivery: any) => {
				return delivery.status !== 'success' && delivery.status !== 'failed'
			})
		})
	})

	onBeforeMount(() => {
		refresh()
	})

	onUnmounted(() => {
		clearInterval(statusInterval)
	})

	function setStatusInterval() {
		if (!intervalShouldBeRunning.value) {
			if (statusInterval) clearInterval(statusInterval)
			statusInterval = null
			return
		}
		if (statusInterval) return
		statusInterval = setInterval(async () => {
			if (!intervalShouldBeRunning.value) {
				clearInterval(statusInterval)
				statusInterval = null
				return
			}
			await refresh()
		}, 2000)
	}

	async function refresh() {
		await props.listState.getList()
		setStatusInterval()
		await nextTick()
		openRowFromQueryParams()
	}

	return { refresh }
}

function openRowFromQueryParams() {
	if (props.listState.isLoading.value || !props.listState.isLoaded) return
	if (route.query.exportId && typeof route.query.exportId === 'string') {
		const success = ensureRowOpen(route.query.exportId)

		if (!success) return
		const newQuery = { ...route.query }
		delete newQuery['exportId']

		router.replace({
			path: route.path,
			query: newQuery,
		})
	}
}

function ensureRowOpen(id: number | string) {
	const rowToggleButton = document
		.querySelector(`.p-row-even:has(#export-${id})`)
		?.querySelector('.p-datatable-row-toggle-button') as HTMLElement | undefined
	if (!rowToggleButton) return false
	const isOpen = ((rowToggleButton.attributes as any)['aria-expanded'] as any).value === 'true'
	if (!isOpen) rowToggleButton.click()
	return true
}
</script>

<style lang="scss" scoped>
.exports-table {
	width: 100%;

	.exports-table__app {
		display: flex;
		flex-direction: column;
		row-gap: 2px;
		align-items: start;

		.exports-table__app-uuid {
			font-size: 11px;
			color: var(--text-color);
			text-decoration: none;
			opacity: 0.8;
			font-family: monospace;
			text-wrap: nowrap;
		}

		.exports-table__app-name {
			font-weight: 600;
		}
	}

	.exports-table__date {
		display: flex;
		align-items: start;
		gap: 2px;
		flex-direction: column;

		.exports-table__date-date {
			font-weight: 500;
		}
		.exports-table__date-link {
			font-size: 11px;
			color: var(--text-color);
			text-decoration: none;
			opacity: 0.8;
			&:hover {
				text-decoration: underline;
			}
		}
	}

	.exports-table__actions {
		display: flex;
		justify-content: flex-end;
		column-gap: 8px;
	}

	.exports-table__version {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: center;

		.exports-table__muted {
			color: var(--p-text-muted-color);
		}
	}

	.exports-table__delivery {
		display: flex;
		flex-direction: column;

		.exports-table__delivery-type {
			text-transform: capitalize;
			font-weight: 600;
		}
		.exports-table__delivery-date {
			font-size: 12px;
			color: var(--text-color-secondary);
		}
	}

	.exports-table__destination {
		display: inline-flex;
		align-items: center;
		column-gap: 8px;
		&--git svg {
			color: #ffffff;
		}
		&--zip svg {
			color: #c8c8c8;
		}
	}

	.exports-table__status-tag {
		width: 100%;
	}
}
</style>
